import { useCallback, useState } from "react";
import { useLocation, useSearchParams, type Location } from "react-router";
import type { SignInOutput } from "aws-amplify/auth";
import { SignInForm } from "../components/auth/sign-in-form.js";
import { MfaChallengeForm } from "../components/auth/mfa-challenge-form.js";
import { MfaSetupFlow } from "../components/auth/mfa-setup-flow.js";
import { ErrorMessage } from "../components/auth/error-message.js";

/**
 * Sign-in page. Holds the auth flow state machine: SignInForm →
 * (DONE | TOTP_CODE | TOTP_SETUP). On success, performs a hard
 * window.location navigation back to the originally-requested URL
 * (preserved by ProtectedRoute in location.state.from), defaulting
 * to /dashboard.
 */
export function SignInPage() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState<SignInOutput | null>(null);
  const [accountName, setAccountName] = useState<string>("");

  type RouteState = { from?: Location };
  const stateFrom = (location.state as RouteState | null)?.from?.pathname;
  // Phase 13.5: honor `?next=/somewhere` over the React-Router state.from
  // so the welcome-back-summary email's `?next=/fire` lands on /fire after
  // sign-in. We constrain `next` to in-app paths (must start with `/` and
  // not `//`) to avoid open-redirect.
  const nextParam = searchParams.get("next");
  const safeNext =
    nextParam && nextParam.startsWith("/") && !nextParam.startsWith("//")
      ? nextParam
      : null;
  const redirectTo = safeNext ?? stateFrom ?? "/dashboard";

  const onSignedIn = useCallback(() => {
    // Hard navigation: full page reload so Amplify's singleton
    // re-initializes from the freshly-stored tokens. Avoids the
    // fetchAuthSession race that otherwise leaves the auth context
    // out of sync after confirmSignIn / TOTP verify resolves.
    window.location.assign(redirectTo);
  }, [redirectTo]);

  const onResult = useCallback(
    (result: SignInOutput, creds: { username: string; password: string }) => {
      setAccountName(creds.username);
      if (result.isSignedIn) {
        void onSignedIn();
        return;
      }
      setStep(result);
    },
    [onSignedIn],
  );

  return (
    <div className="min-h-screen bg-[var(--color-page)] text-[var(--color-text)]">
      <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center p-6">
        <div className="mb-8 text-center">
          <h1 className="font-display text-3xl font-medium tracking-tight">
            Hearth
          </h1>
          <p className="mt-1 text-sm text-[var(--color-muted)]">
            Sign in to your household
          </p>
        </div>

        {renderStep(step, accountName, onSignedIn)}

        <p className="mt-6 text-center text-sm text-[var(--color-muted)]">
          Don't have an account?{" "}
          <a
            href="/signup"
            className="text-[var(--color-accent)] hover:underline"
          >
            Sign up
          </a>
        </p>
      </div>
    </div>
  );

  function renderStep(
    s: SignInOutput | null,
    name: string,
    onDone: () => void,
  ) {
    if (s === null)
      return (
        <SignInForm
          onResult={onResult}
          onAlreadySignedIn={() => onDone()}
        />
      );
    switch (s.nextStep.signInStep) {
      case "CONFIRM_SIGN_IN_WITH_TOTP_CODE":
        return <MfaChallengeForm onSignedIn={() => void onDone()} />;
      case "CONTINUE_SIGN_IN_WITH_TOTP_SETUP":
        return (
          <MfaSetupFlow
            nextStep={s.nextStep}
            accountName={name}
            onSignedIn={() => void onDone()}
          />
        );
      default:
        return (
          <ErrorMessage>
            This sign-in step isn't handled yet ({s.nextStep.signInStep}).
            Please contact support.
          </ErrorMessage>
        );
    }
  }
}
